import { useCallback, useEffect, useId, useState, useMemo, useRef, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { useForm, useFieldArray, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, Trash2, Stethoscope, Eye, Pencil } from "lucide-react";
import { ProfileDataModal } from "./ProfileDataModal";
import { SIPEAGROGuideDialog } from "./SIPEAGROGuideDialog";
import { useProfileValidation } from "@/hooks/useProfileValidation";
import { validatePrescriptionProfile } from "@/lib/prescriptions/profile-validation";
import type { Profile } from "@/hooks/useProfile";
import { useQueryClient } from "@tanstack/react-query";
import type { PrescriptionType } from "@/lib/validations/medical-prescription";
import { useAuth } from "@/contexts/AuthContext";
import { useUserA1Certificate } from "@/hooks/useUserA1Certificate";
import { useSignPdf } from "@/hooks/useSignPdf";
import { Button } from "@akuna/ui";
import { Textarea } from "@/components/ui/textarea";
import { Autocomplete } from "@/components/ui/autocomplete";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Separator } from "@akuna/ui";
import {
  medicalPrescriptionSchema,
  type MedicalPrescriptionInput,
  type PrescriptionItemInput,
  type ManipulatedPayloadInput,
  type ControlRegime,
} from "@/lib/validations/medical-prescription";
import {
  useCreateMedicalPrescription,
  useDeleteMedicalPrescription,
  useMedicalPrescriptions,
  useUpdateMedicalPrescription,
  type PrescriptionWithItems,
} from "@/hooks/useMedicalPrescriptions";
import {
  useCreatePrescriptionTemplate,
  usePrescriptionTemplates,
  type PrescriptionTemplateWithItems,
} from "@/hooks/usePrescriptionTemplates";
import { PrescriptionViewDialog } from "./PrescriptionViewDialog";
import { ExamPrescriptionComposer } from "./ExamPrescriptionComposer";
import {
  useMedications,
} from "@/hooks/useMedications";
import { ManipulatedFormulaEditor } from "./editor/ManipulatedFormulaEditor";
import { PrescriptionMetaFields } from "./editor/PrescriptionMetaFields";
import { SaveAsModelButton } from "./editor/SaveAsModelButton";
import {
  type MedicationWithRelations,
} from "./MedicationAutocomplete";
import { CreateMedicationDialog } from "./CreateMedicationDialog";
import {
  MedicationListEditor,
  PrescriptionTypeSelector,
  type EditorKind,
  type ExpandedState,
} from "./editor/PrescriptionEditor";
import { usePublishPrescriptionDraft } from "./editor/PrescriptionDraftContext";
import { MobileActionBar, MobileSheetView, MobileViewToggle, type MobileView } from "./editor/MobilePrescriptionEditor";
import { useIsMobile } from "@/hooks/use-mobile";
import type { BuyerSnapshot } from "@/lib/validations/medical-prescription";
import type { Database } from "@/integrations/supabase/types";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  mapManipulatedTemplatePayload,
  mapStandardTemplateItems,
} from "@/lib/prescriptions/prescription-template-composition";
import { buildPosologySummary } from "@/lib/prescriptions/posology";
import { cn } from "@/lib/utils";

type MedicationRow = Database["public"]["Tables"]["medications"]["Row"];

const DEFAULT_MANIPULATED_PAYLOAD: ManipulatedPayloadInput = {
  components: [{ medication_id: "", name: "", concentration: "" }],
  pharmaceutical_form: "",
  treatment_days: "",
  total_doses: "",
  route: "",
};

function getLookupName(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    return typeof name === "string" ? name : "";
  }
  return "";
}

function formatMedicationSummary(item: {
  medication_name?: string | null;
  presentation?: string | null;
  posology_mode?: string | null;
  free_text_posology?: string | null;
  dose_amount?: string | null;
  dose_unit?: string | null;
  frequency_value?: string | null;
  frequency_unit?: string | null;
  duration_value?: string | null;
  duration_unit?: string | null;
}) {
  if (item.medication_name?.trim() || item.presentation?.trim()) return item.medication_name ?? "";
  return buildPosologySummary(item, (_key, options) => {
    if (_key === "posologyEvery") return `a cada ${options?.value ?? ""} ${options?.unit ?? ""}`.trim();
    if (_key === "posologyFor") return `por ${options?.value ?? ""} ${options?.unit ?? ""}`.trim();
    if (_key === "posologyAutomaticSentence") {
      return `Dar ${options?.dose ?? ""}${options?.frequency ?? ""}${options?.duration ?? ""}`.trim();
    }
    if (_key === "posologyDoseFallback") return "conforme orientação";
    return "Posologia não informada.";
  });
}

interface PrescriptionComposerProps {
  petId: string;
  clinicId: string;
  appointmentId?: string;
  medicalRecordId?: string;
  speciesId?: string;
  onCreated?: () => void;
  onCreatedId?: (id: string) => void;
  onCreatedPrescription?: (prescription: PrescriptionWithItems) => void;
  onUpdated?: () => void;
  onUpdatedId?: (id: string) => void;
  autoOpenPreviewOnCreate?: boolean;
  isInsideDialog?: boolean;
  initialPrescription?: PrescriptionWithItems | null;
  variant?: "desktop" | "mobile";
  /** When provided, the Limpar/Salvar footer buttons are portaled here instead of rendered inline. Used by modal callers that render their own fixed footer. */
  footerContainer?: HTMLDivElement | null;
}

const isBlankPrescriptionItem = (item: PrescriptionItemInput) =>
  !item.medication_id?.trim() &&
  !item.medication_name?.trim() &&
  !item.medication_concentration?.trim() &&
  !item.pharmaceutical_form?.trim() &&
  !item.administration_route?.trim() &&
  !item.presentation?.trim() &&
  !item.free_text_posology?.trim() &&
  !item.dose_amount?.trim() &&
  !item.dose_unit?.trim() &&
  !item.frequency_value?.trim() &&
  !item.frequency_unit?.trim() &&
  !item.duration_value?.trim() &&
  !item.duration_unit?.trim();

const isBlankManipulatedComponent = (component: ManipulatedPayloadInput["components"][number]) =>
  !component.medication_id?.trim() &&
  !component.name?.trim() &&
  !component.concentration?.trim() &&
  !component.free_text?.trim();

export const PrescriptionComposer = ({
  petId,
  clinicId,
  appointmentId,
  medicalRecordId,
  speciesId,
  onCreated,
  onCreatedId,
  onCreatedPrescription,
  onUpdated,
  onUpdatedId,
  autoOpenPreviewOnCreate = true,
  isInsideDialog = false,
  initialPrescription = null,
  variant = "desktop",
  footerContainer = null,
}: PrescriptionComposerProps) => {
  const { t, i18n } = useTranslation("prescriptions");
  const formId = useId();
  const { user } = useAuth();
  const { data: a1Certificate } = useUserA1Certificate();
  const hasCertificate = Boolean(a1Certificate);
  const signPdf = useSignPdf();
  const [signingPrescriptionId, setSigningPrescriptionId] = useState<string | null>(null);
  const [medicationSearch, setMedicationSearch] = useState("");
  const [, setActiveMedicationId] = useState<string | undefined>();
  const [templateSearch, setTemplateSearch] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState<string | null>(null);
  const [selectedPrescriptionDraft, setSelectedPrescriptionDraft] = useState<PrescriptionWithItems | null>(null);
  const [prescriptionToDeleteId, setPrescriptionToDeleteId] = useState<string | null>(null);
  const [editingPrescriptionId, setEditingPrescriptionId] = useState<string | null>(initialPrescription?.id ?? null);
  const [showPrescriptionDialog, setShowPrescriptionDialog] = useState(false);
  const [showDeletePrescriptionDialog, setShowDeletePrescriptionDialog] = useState(false);
  const [prescriptionMode, setPrescriptionMode] = useState<"standard" | "manipulated" | "exam">("standard");
  const [suggestedRegime, setSuggestedRegime] = useState<ControlRegime | null>(null);
  const [examValidityInDays, setExamValidityInDays] = useState<number | undefined>(30);
  const [showProfileDataModal, setShowProfileDataModal] = useState(false);
  const [showSIPEAGRODialog, setShowSIPEAGRODialog] = useState(false);
  const [pendingPrescriptionData, setPendingPrescriptionData] = useState<MedicalPrescriptionInput | null>(null);
  const [showCreateMedicationDialog, setShowCreateMedicationDialog] = useState(false);
  const [medicationNameToCreate, setMedicationNameToCreate] = useState("");
  const [medicationCreateContext, setMedicationCreateContext] = useState<{
    source: "standard" | "manipulated";
    index?: number;
  } | null>(null);
  const [expanded, setExpanded] = useState<ExpandedState | null>(null);
  const [draftItem, setDraftItem] = useState<PrescriptionItemInput | null>(null);
  const [mobileView, setMobileView] = useState<MobileView>("edit");
  const [draftPayload, setDraftPayload] = useState<ManipulatedPayloadInput | null>(null);
  const [createMedicationCallback, setCreateMedicationCallback] = useState<{ run: (medication: MedicationRow) => void } | null>(null);
  const templateType = prescriptionMode === "manipulated" ? "manipulated" : "standard";
  const form = useForm<MedicalPrescriptionInput>({
    resolver: zodResolver(medicalPrescriptionSchema),
    defaultValues: {
      pet_id: petId,
      clinic_id: clinicId,
      appointment_id: appointmentId ?? "",
      medical_record_id: medicalRecordId ?? "",
      type: "standard",
      control_program: "",
      control_identifier: "",
      control_regime: "none" as ControlRegime,
      validity_in_days: 30,
      observations: "",
      items: [],
      buyer_mode: "blank",
      buyer_snapshot: null,
      manipulated_payload: DEFAULT_MANIPULATED_PAYLOAD,
    },
  });

  const {
    fields: itemFields,
    append,
    remove,
    replace,
    update: updateItem,
  } = useFieldArray({
    control: form.control,
    name: "items",
  });

  const createPrescription = useCreateMedicalPrescription();
  const deletePrescription = useDeleteMedicalPrescription();
  const updatePrescription = useUpdateMedicalPrescription();
  const { data: medications = [] } = useMedications({
    searchTerm: medicationSearch,
    includeControlled: true,
  });
  const {
    data: prescriptionTemplates = [],
    isLoading: isLoadingTemplates,
  } = usePrescriptionTemplates(clinicId, templateType);

  useEffect(() => {
    setSelectedTemplateId(null);
    setTemplateSearch("");
  }, [clinicId, templateType]);

  const templatesById = useMemo(() => {
    return new Map(prescriptionTemplates.map((template) => [template.id, template]));
  }, [prescriptionTemplates]);

  const templateOptions = useMemo(() => {
    return prescriptionTemplates.map((template) => ({
      label: template.title,
      value: template.id,
    }));
  }, [prescriptionTemplates]);

  const {
    data: existingPrescriptions = [],
    isLoading: isLoadingPrescriptions,
  } = useMedicalPrescriptions(petId, appointmentId, medicalRecordId);

  const selectedPrescription = useMemo(() => {
    if (!selectedPrescriptionId) return null;
    return existingPrescriptions.find((p) => p.id === selectedPrescriptionId)
      ?? (selectedPrescriptionDraft?.id === selectedPrescriptionId ? selectedPrescriptionDraft : null);
  }, [existingPrescriptions, selectedPrescriptionDraft, selectedPrescriptionId]);

  const editingPrescription = useMemo(() => {
    if (initialPrescription) return initialPrescription;
    if (!editingPrescriptionId) return null;
    return existingPrescriptions.find((p) => p.id === editingPrescriptionId) ?? null;
  }, [editingPrescriptionId, existingPrescriptions, initialPrescription]);

  const prescriptionToDelete = useMemo(() => {
    if (!prescriptionToDeleteId) return null;
    return existingPrescriptions.find((p) => p.id === prescriptionToDeleteId) ?? null;
  }, [prescriptionToDeleteId, existingPrescriptions]);

  const isEditMode = Boolean(editingPrescription);

  const buildCreateDefaults = useMemo(
    () => ({
      pet_id: petId,
      clinic_id: clinicId,
      appointment_id: appointmentId ?? "",
      medical_record_id: medicalRecordId ?? "",
      type: "standard" as const,
      control_program: "",
      control_identifier: "",
      control_regime: "none" as const,
      validity_in_days: 30,
      observations: "",
      items: [],
      buyer_mode: "blank" as const,
      buyer_snapshot: null,
      manipulated_payload: DEFAULT_MANIPULATED_PAYLOAD,
    }),
    [appointmentId, clinicId, medicalRecordId, petId],
  );

  const mapPrescriptionToFormValues = (prescription: PrescriptionWithItems): MedicalPrescriptionInput => {
    const baseValues = {
      pet_id: prescription.pet_id,
      clinic_id: prescription.clinic_id,
      appointment_id: prescription.appointment_id ?? "",
      medical_record_id: prescription.medical_record_id ?? "",
      validity_in_days: prescription.validity_in_days ?? 30,
      observations: prescription.observations ?? "",
      control_regime: (prescription as unknown as { control_regime?: ControlRegime }).control_regime ?? "none" as ControlRegime,
      buyer_mode: ((prescription as { buyer_mode?: string | null }).buyer_mode === "filled" ? "filled" : "blank") as "blank" | "filled",
      buyer_snapshot: ((prescription as { buyer_snapshot?: BuyerSnapshot | null }).buyer_snapshot ?? null) as BuyerSnapshot | null,
    };

    if (prescription.type === "manipulated") {
      const payload = (prescription.manipulated_payload as ManipulatedPayloadInput | null) ?? DEFAULT_MANIPULATED_PAYLOAD;
      return {
        ...baseValues,
        type: "manipulated",
        control_program: "",
        control_identifier: "",
        items: [],
        manipulated_payload: {
          components: payload.components?.length
            ? payload.components.map((component, index) => ({
                medication_id: component.medication_id ?? prescription.items[index]?.medication_id ?? "",
                name: component.name ?? prescription.items[index]?.medication_name ?? "",
                concentration: component.concentration ?? prescription.items[index]?.medication_concentration ?? "",
                free_text: component.free_text ?? "",
              }))
            : prescription.items.map((item) => ({
                medication_id: item.medication_id ?? "",
                name: item.medication_name ?? "",
                concentration: item.medication_concentration ?? "",
              })),
          pharmaceutical_form: payload.pharmaceutical_form ?? "",
          treatment_days: payload.treatment_days ?? "",
          total_doses: payload.total_doses ?? "",
          route: payload.route ?? "",
          instructions: payload.instructions ?? "",
          free_text: payload.free_text ?? "",
        },
      };
    }

    return {
      ...baseValues,
      type: prescription.type === "controlled" ? "controlled" : "standard",
      control_program: prescription.control_program ?? "",
      control_identifier: prescription.control_identifier ?? "",
      items: prescription.items.length > 0
        ? prescription.items.map((item) => ({
            medication_id: item.medication_id ?? "",
            medication_name: item.medication_name ?? "",
            medication_concentration: item.medication_concentration ?? "",
            pharmaceutical_form: item.pharmaceutical_form ?? "",
            administration_route: item.administration_route ?? "",
            presentation: item.presentation ?? "",
            posology_mode: item.posology_mode ?? "automatic",
            free_text_posology: item.free_text_posology ?? "",
            dose_amount: item.dose_amount ?? "",
            dose_unit: item.dose_unit ?? "",
            frequency_value: item.frequency_value ?? "",
            frequency_unit: item.frequency_unit ?? "",
            duration_value: item.duration_value ?? "",
            duration_unit: item.duration_unit ?? "",
          }))
        : [],
      manipulated_payload: DEFAULT_MANIPULATED_PAYLOAD,
    };
  };

  const loadedRevision = useRef<string | null>(null);
  const initializedEditor = useRef<string | null>(null);

  const resetComposerForm = useCallback((prescription: PrescriptionWithItems | null) => {
    loadedRevision.current = prescription?.updated_at ?? null;
    setExpanded(null);
    setDraftItem(null);
    setDraftPayload(null);
    if (!prescription) {
      form.reset(buildCreateDefaults);
      setPrescriptionMode("standard");
      setExamValidityInDays(30);
      setActiveMedicationId(undefined);
      setTemplateSearch("");
      setSelectedTemplateId(null);
      return;
    }

    if (prescription.type === "exam") {
      setPrescriptionMode("exam");
      setExamValidityInDays(prescription.validity_in_days ?? 30);
      setTemplateSearch("");
      setSelectedTemplateId(null);
      setActiveMedicationId(undefined);
      return;
    }

    const values = mapPrescriptionToFormValues(prescription);
    form.reset(values);
    setPrescriptionMode(values.type === "manipulated" ? "manipulated" : "standard");
    setExamValidityInDays(values.validity_in_days);
    setTemplateSearch("");
    setSelectedTemplateId(null);
    setActiveMedicationId(values.type === "manipulated" ? undefined : values.items[0]?.medication_id || undefined);
  }, [buildCreateDefaults, form]);

  const handleMedicationSelection = (
    index: number,
    medication: MedicationRow,
    displayName?: string,
  ) => {
    const typedMedication = medication as unknown as MedicationWithRelations;
    updateItem(index, {
      ...form.getValues(`items.${index}`),
      medication_id: medication.id,
      medication_name: displayName ?? medication.name,
      medication_concentration: medication.concentration ?? "",
      pharmaceutical_form: getLookupName(typedMedication.pharmaceutical_form),
      administration_route: getLookupName(typedMedication.administration_route),
      dose_unit:
        form.getValues(`items.${index}.dose_unit`) ||
        (medication.dose_unit ?? ""),
    });

    setActiveMedicationId(medication.id);
    form.clearErrors(`items.${index}.medication_name`);
  };

  const applyTemplate = (template: PrescriptionTemplateWithItems) => {
    if (template.template_type === "manipulated") {
      form.setValue("type", "manipulated");
      setPrescriptionMode("manipulated");
      form.setValue("control_program", "");
      form.setValue("control_identifier", "");
      form.setValue("observations", template.observations ?? "");
      form.setValue("manipulated_payload", mapManipulatedTemplatePayload(template));
      replace([]);
      setActiveMedicationId(undefined);
      form.clearErrors("manipulated_payload");
      return;
    }

    const mappedItems = mapStandardTemplateItems(template, i18n.language);

    form.setValue("type", "standard");
    setPrescriptionMode("standard");
    form.setValue("control_program", "");
    form.setValue("control_identifier", "");
    form.setValue("observations", template.observations ?? "");
    form.setValue("manipulated_payload", DEFAULT_MANIPULATED_PAYLOAD);

    if (mappedItems.length === 0) {
      replace([]);
      setActiveMedicationId(undefined);
    } else {
      replace(mappedItems);
      setActiveMedicationId(mappedItems[0]?.medication_id || undefined);
    }

    form.clearErrors("items");
  };

  const prescriptionType = form.watch("type") as PrescriptionType;
  const controlRegime = form.watch("control_regime") as string | undefined;
  const watchedItems = useWatch({ control: form.control, name: "items" });
  const validation = useProfileValidation(prescriptionType, controlRegime);
  const queryClient = useQueryClient();

  const editorKind: EditorKind =
    prescriptionMode === "standard" && controlRegime === "controlled_human" ? "controlled" : prescriptionMode;

  const handleKindChange = (next: EditorKind) => {
    if (next === editorKind) return;
    setExpanded(null);
    setDraftItem(null);
    if (next === "exam") {
      setPrescriptionMode("exam");
      return;
    }
    if (next === "controlled") {
      if (prescriptionMode !== "standard") handleMedicationTypeChange("standard");
      form.setValue("control_regime", "controlled_human" as ControlRegime, { shouldValidate: true });
      form.setValue("validity_in_days", 30, { shouldValidate: true });
      return;
    }
    if (prescriptionMode === "exam" && next === "standard") {
      setPrescriptionMode("standard");
    }
    handleMedicationTypeChange(next);
    form.setValue("buyer_mode", "blank");
    form.setValue("buyer_snapshot", null);
  };

  const openNewItem = () => setExpanded({ mode: "new" });
  const openEditItem = (index: number) => setExpanded({ mode: "edit", index });
  const closeExpanded = () => {
    setExpanded(null);
    setDraftItem(null);
    setDraftPayload(null);
  };
  const commitItem = (item: PrescriptionItemInput) => {
    if (expanded?.mode === "edit") {
      updateItem(expanded.index, item);
    } else {
      append(item);
    }
    form.clearErrors("items");
    closeExpanded();
  };
  const removeItem = (index: number) => {
    remove(index);
    if (expanded?.mode === "edit") closeExpanded();
  };
  const duplicateItem = (index: number) => {
    const source = form.getValues(`items.${index}`);
    if (!source) return;
    const next = [...(form.getValues("items") ?? [])];
    next.splice(index + 1, 0, { ...source });
    replace(next);
  };
  const moveItem = (from: number, to: number) => {
    const next = [...(form.getValues("items") ?? [])];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    replace(next);
  };
  const openCreateMedicationFor = (name: string, onCreated: (medication: MedicationRow) => void) => {
    setMedicationNameToCreate(name);
    setCreateMedicationCallback({ run: onCreated });
    setMedicationCreateContext(null);
    setShowCreateMedicationDialog(true);
  };

  const draftValues = form.watch();
  const draftHighlight = useMemo(() => {
    if (prescriptionMode === "manipulated") return draftPayload ? { kind: "formula" as const, index: 0 } : null;
    if (!draftItem || prescriptionMode !== "standard") return null;
    const count = (watchedItems ?? []).length;
    return { kind: "medication" as const, index: expanded?.mode === "edit" ? expanded.index : count };
  }, [draftItem, draftPayload, expanded, prescriptionMode, watchedItems]);
  const publishedDraft = useMemo(() => {
    if (prescriptionMode === "exam") return null;
    if (prescriptionMode === "manipulated") {
      return {
        values: { ...draftValues, manipulated_payload: draftPayload ?? draftValues.manipulated_payload } as MedicalPrescriptionInput,
        highlight: draftHighlight,
      };
    }
    const current = (draftValues.items ?? []) as PrescriptionItemInput[];
    const nextItems = draftItem
      ? expanded?.mode === "edit"
        ? current.map((entry, index) => (index === expanded.index ? draftItem : entry))
        : [...current, draftItem]
      : current;
    return { values: { ...draftValues, items: nextItems } as MedicalPrescriptionInput, highlight: draftHighlight };
  }, [draftValues, draftItem, draftPayload, expanded, draftHighlight, prescriptionMode]);
  // Pages mount the desktop and mobile composers together (one hidden by CSS); only the visible one publishes.
  const isMobileViewport = useIsMobile();
  usePublishPrescriptionDraft(publishedDraft, (variant === "mobile") === isMobileViewport && prescriptionMode !== "exam");

  const handlePrescriptionCreated = (prescription?: PrescriptionWithItems | null) => {
    if (prescription?.id && autoOpenPreviewOnCreate) {
      console.info("[prescriptions] opening-preview", {
        prescriptionId: prescription.id,
        petId,
        clinicId,
        appointmentId: appointmentId ?? null,
        medicalRecordId: medicalRecordId ?? null,
      });
      setSelectedPrescriptionDraft(prescription);
      setSelectedPrescriptionId(prescription.id);
      setShowPrescriptionDialog(true);
    }
    if (prescription?.id) onCreatedId?.(prescription.id);
    if (prescription) onCreatedPrescription?.(prescription);
    onCreated?.();
  };

  const handlePrescriptionUpdated = (prescriptionId?: string | null) => {
    if (prescriptionId) onUpdatedId?.(prescriptionId);
    onUpdated?.();
    if (!initialPrescription) {
      setEditingPrescriptionId(null);
      resetComposerForm(null);
    }
  };

  const handleDeletePrescription = async () => {
    if (!prescriptionToDeleteId) return;

    try {
      await deletePrescription.mutateAsync(prescriptionToDeleteId);
      if (selectedPrescriptionId === prescriptionToDeleteId) {
        setSelectedPrescriptionId(null);
        setSelectedPrescriptionDraft(null);
        setShowPrescriptionDialog(false);
      }
      setShowDeletePrescriptionDialog(false);
      setPrescriptionToDeleteId(null);
    } catch {
      // Toast feedback is handled in the mutation.
    }
  };

  useEffect(() => {
    const source = initialPrescription ?? editingPrescription;
    if (editingPrescriptionId && !source) return;
    const context = [petId, clinicId, appointmentId ?? "", medicalRecordId ?? "", source?.id ?? "new"].join(":");
    if (initializedEditor.current === context) return;
    initializedEditor.current = context;
    if (initialPrescription) {
      setEditingPrescriptionId(initialPrescription.id);
      resetComposerForm(initialPrescription);
      return;
    }

    if (!editingPrescriptionId) {
      resetComposerForm(null);
      return;
    }

    if (editingPrescription) {
      resetComposerForm(editingPrescription);
    }
  }, [editingPrescription, editingPrescriptionId, initialPrescription, resetComposerForm, petId, clinicId, appointmentId, medicalRecordId]);

  // Auto-suggest control regime based on selected medications
  useEffect(() => {
    if (prescriptionMode !== "standard") return;
    const itemMedIds = watchedItems
      ?.map((item) => item.medication_id)
      .filter((id): id is string => Boolean(id && id.trim() !== "")) ?? [];

    if (itemMedIds.length === 0) {
      setSuggestedRegime(null);
      return;
    }

    const selectedMeds = medications.filter((med) => itemMedIds.includes(med.id));
    const hasMapaNotification = selectedMeds.some((med) => med.requires_map_notification);

    if (hasMapaNotification) {
      setSuggestedRegime("controlled_vet_mapa");
    } else {
      setSuggestedRegime(null);
    }
  }, [watchedItems, medications, prescriptionMode, form]);

  const handleMedicationTypeChange = (value: "standard" | "manipulated") => {
    form.setValue("type", value);
    setPrescriptionMode(value);
    // Reset control_regime when switching modes to avoid stale regime persisting
    form.setValue("control_regime", "none" as ControlRegime);
    if (value === "manipulated") {
      form.setValue("items", []);
      for (let idx = itemFields.length - 1; idx >= 0; idx -= 1) {
        remove(idx);
      }
      const existingPayload = form.getValues("manipulated_payload");
      const hasComponents =
        !!existingPayload &&
        typeof existingPayload === "object" &&
        "components" in existingPayload &&
        Array.isArray((existingPayload as ManipulatedPayloadInput).components) &&
        (existingPayload as ManipulatedPayloadInput).components.length > 0;

      if (!hasComponents) {
        form.setValue("manipulated_payload", DEFAULT_MANIPULATED_PAYLOAD);
      }
      setTemplateSearch("");
      setSelectedTemplateId(null);
    }
    setExpanded(null);
  };

  const handleSavePrescription = async (data: MedicalPrescriptionInput, profileOverride?: Profile | null) => {
    // Incluir CRMV e MAPA do perfil na receita
    const cachedProfile = user?.id
      ? queryClient.getQueryData<Profile | null>(["profile", user.id])
      : null;
    const profileData = profileOverride ?? cachedProfile ?? validation.profile;
    // Construir CRMV completo a partir de estado e número
    const crmvFull = profileData?.crmv_state && profileData?.crmv_number
      ? `${profileData.crmv_state}${profileData.crmv_number}`
      : null;
    // Helper function to convert empty strings to null/undefined
    const normalizeId = (id?: string | null): string | undefined => {
      if (!id || id.trim() === "") return undefined;
      return id;
    };

    const prescriptionPayload = {
      ...data,
      appointment_id: normalizeId(data.appointment_id),
      medical_record_id: normalizeId(data.medical_record_id),
      crmv: crmvFull,
      mapa_number: profileData?.mapa_number || null,
      control_regime: data.control_regime ?? "none",
    };

    if (editingPrescription) {
      const updatedPrescription = await updatePrescription.mutateAsync({
        ...prescriptionPayload,
        id: editingPrescription.id,
        expected_updated_at: loadedRevision.current,
        vet_user_id: editingPrescription.vet_user_id,
      });
      handlePrescriptionUpdated(updatedPrescription?.id);
      return;
    }

    const newPrescription = await createPrescription.mutateAsync({
      ...prescriptionPayload,
      issuer_address: profileData?.address?.trim() || null,
    });
    console.info("[prescriptions] created-flow-finished", {
      prescriptionId: newPrescription.id,
      petId,
      clinicId,
      appointmentId: appointmentId ?? null,
      medicalRecordId: medicalRecordId ?? null,
    });

    const nextType = data.type;
    const nextItems: PrescriptionItemInput[] = [];

    form.reset({
      ...form.getValues(),
      type: nextType,
      observations: "",
      control_identifier: "",
      items: nextItems,
      manipulated_payload: DEFAULT_MANIPULATED_PAYLOAD,
    });
    setActiveMedicationId(undefined);
    setTemplateSearch("");
    setSelectedTemplateId(null);

    handlePrescriptionCreated(newPrescription);
  };

  const onSubmit = async (data: MedicalPrescriptionInput) => {
    if (expanded) {
      toast.info(t("editor.finishOpenItem"));
      return;
    }
    // controlled_vet_mapa: block emission, show SIPEAGRO guide
    if (data.control_regime === "controlled_vet_mapa") {
      setPendingPrescriptionData(data);
      setShowSIPEAGRODialog(true);
      return;
    }

    const validationResult = validation.validate();

    // For manipulated controlled, also require MAPA number
    if (data.control_regime === "manipulated_controlled_mapa") {
      if (!validationResult.isValid || validationResult.needsMAPA) {
        setPendingPrescriptionData(data);
        setShowProfileDataModal(true);
        return;
      }
    }

    // Para receitas controladas, mostrar guia do SIPEAGRO
    if (data.type === "controlled") {
      setPendingPrescriptionData(data);
      setShowSIPEAGRODialog(true);
      return;
    }

    // Para receitas padrão e manipulados, validar campos do perfil
    if (!validationResult.isValid) {
      setPendingPrescriptionData(data);
      setShowProfileDataModal(true);
      return;
    }

    // Se tudo estiver válido, criar a receita
    await handleSavePrescription(data);
  };

  const handleMedicalPrescriptionFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    const currentType = form.getValues("type");

    if (currentType === "manipulated") {
      const currentComponents = form.getValues("manipulated_payload")?.components ?? [];
      const filledComponents = currentComponents.filter((component) => !isBlankManipulatedComponent(component));

      if (filledComponents.length !== currentComponents.length) {
        form.setValue("manipulated_payload.components", filledComponents, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    } else {
      const currentItems = form.getValues("items") ?? [];
      const filledItems = currentItems.filter((item) => !isBlankPrescriptionItem(item));

      if (filledItems.length > 0 && filledItems.length !== currentItems.length) {
        form.setValue("items", filledItems, { shouldDirty: true, shouldValidate: false });
        replace(filledItems);
      }
    }

    void form.handleSubmit(onSubmit, () => setMobileView("edit"))(event);
  };

  const handleProfileDataComplete = async () => {
    if (!pendingPrescriptionData) return;

    // Fechar o modal primeiro para evitar reabertura
    setShowProfileDataModal(false);

    // Refetch do perfil para garantir dados atualizados antes de criar a receita.
    if (user?.id) {
      await queryClient.refetchQueries({ queryKey: ["profile", user.id] });
    } else {
      await queryClient.refetchQueries({ queryKey: ["profile"] });
    }

    const profileKey = user?.id ? (["profile", user.id] as const) : (["profile"] as const);
    const profileFromCache = queryClient.getQueryData<Profile | null>(profileKey);

    const refreshedValidation = validatePrescriptionProfile(
      profileFromCache,
      pendingPrescriptionData.type,
      pendingPrescriptionData.control_regime,
    );

    if (refreshedValidation.isValid) {
      // Criar a receita se estiver válido
      await handleSavePrescription(pendingPrescriptionData, profileFromCache);
      setPendingPrescriptionData(null);
    } else {
      // Se ainda não estiver válido, reabrir o modal
      // Isso só deve acontecer em casos raros (ex: dados não foram salvos corretamente)
      setShowProfileDataModal(true);
    }
  };

  const handleSIPEAGROConfirm = () => {
    form.setValue("control_regime", "none");
    setPendingPrescriptionData(null);
    setShowSIPEAGRODialog(false);
    toast.info(t("composer.controlRegime.controlledVetMapaBlocked"));
  };

  const handleMedicationCreated = (medication: MedicationRow) => {
    if (createMedicationCallback) {
      createMedicationCallback.run(medication);
      setCreateMedicationCallback(null);
      return;
    }
    if (medicationCreateContext?.source === "manipulated" && typeof medicationCreateContext.index === "number") {
      const targetIndex = medicationCreateContext.index;
      form.setValue(`manipulated_payload.components.${targetIndex}.medication_id`, medication.id);
      form.setValue(`manipulated_payload.components.${targetIndex}.name`, medication.name);
      form.clearErrors(`manipulated_payload.components.${targetIndex}.name`);
    } else {
      // Selecionar o medicamento criado automaticamente
      const targetIndex =
        typeof medicationCreateContext?.index === "number"
          ? medicationCreateContext.index
          : itemFields.length - 1;
      if (targetIndex >= 0) {
        handleMedicationSelection(targetIndex, medication, medication.name);
      }
    }
    setMedicationSearch("");
    setMedicationCreateContext(null);
  };

  const handleSignPrescription = async (prescription: typeof existingPrescriptions[number]) => {
    setSigningPrescriptionId(prescription.id);
    try {
      // The authenticated signing service assigns MAPA identifiers atomically.
      const result = await signPdf.mutateAsync({
        resource_type: "prescription",
        resource_id: prescription.id,
        pet_id: petId,
        appointment_id: appointmentId,
        medical_record_id: medicalRecordId,
      });
      const secretCode = result.secret_code ?? result.access_code;
      if (secretCode) {
        toast.success(
          t("sign.successWithCode", {
            code: secretCode,
          }),
        );
      }
    } finally {
      setSigningPrescriptionId(null);
    }
  };

  const createTemplate = useCreatePrescriptionTemplate();
  /** Saves everything written so far (all items + guidance, or the formula) as one reusable model. */
  const saveWholeAsModel = async (title: string) => {
    const values = form.getValues();
    const observations = values.observations ?? "";
    if (values.type === "manipulated") {
      const payload = values.manipulated_payload as ManipulatedPayloadInput | undefined;
      const hasFormula = Boolean(
        payload?.free_text?.trim() || payload?.components?.some((c) => c.name?.trim() || c.free_text?.trim()),
      );
      if (!payload || !hasFormula) {
        toast.error(t("editor.saveModel.empty"));
        throw new Error("empty_model");
      }
      await createTemplate.mutateAsync({
        clinic_id: clinicId,
        title,
        observations,
        template_type: "manipulated",
        items: [],
        manipulated_payload: payload,
      });
      return;
    }
    const items = ((values.items ?? []) as PrescriptionItemInput[]).filter((item) => item.medication_name?.trim());
    if (items.length === 0) {
      toast.error(t("editor.saveModel.empty"));
      throw new Error("empty_model");
    }
    await createTemplate.mutateAsync({
      clinic_id: clinicId,
      title,
      observations,
      template_type: "standard",
      items,
    });
  };
  const saveModelButton = <SaveAsModelButton mobile={variant === "mobile"} onSave={saveWholeAsModel} />;

  const templateSlot = (
    <div className="grid gap-2 md:grid-cols-[1fr_auto] md:items-end">
      <FormItem>
        <FormLabel>{t("composer.template")}</FormLabel>
        <FormControl>
          <Autocomplete
            options={templateOptions}
            value={templateSearch}
            onChange={(value) => {
              setTemplateSearch(value);
              const selected = selectedTemplateId ? templatesById.get(selectedTemplateId) : null;
              if (!selected || selected.title !== value) {
                setSelectedTemplateId(null);
              }
            }}
            onSelect={(option) => {
              const template = templatesById.get(option.value);
              if (!template) return;
              setSelectedTemplateId(option.value);
              applyTemplate(template);
            }}
            selectedValue={selectedTemplateId ?? ""}
            placeholder={t("composer.templatePlaceholder")}
            emptyMessage={isLoadingTemplates ? t("composer.loadingTemplates") : t("composer.emptyTemplates")}
            renderOption={(option) => {
              const template = templatesById.get(option.value);
              const tag = template?.template_type === "manipulated" ? t("composer.templateTagManipulated") : t("composer.templateTagStandard");
              return (
                <div className="flex w-full items-center justify-between gap-2">
                  <span className="truncate">{option.label}</span>
                  <Badge variant="outline" className="shrink-0">
                    {tag}
                  </Badge>
                </div>
              );
            }}
          />
        </FormControl>
      </FormItem>
      {selectedTemplateId && (
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            setSelectedTemplateId(null);
            setTemplateSearch("");
          }}
        >
          {t("composer.clearSelection")}
        </Button>
      )}
    </div>

  );

  return (
    <div className={cn("grid grid-cols-[minmax(0,1fr)]", variant === "mobile" ? "gap-4" : "gap-6")}>
      <div
        className={cn(
          "min-w-0 space-y-4",
          !footerContainer &&
            cn("border border-border/70", variant === "mobile" ? "rounded-2xl bg-card p-4 shadow-sm" : "rounded-lg p-6"),
        )}
      >
        {!isInsideDialog && (
          <div className="flex items-center gap-2 text-lg font-semibold pb-2">
            <Stethoscope className="h-5 w-5" />
            {isEditMode ? t("composer.editPrescription") : t("composer.newPrescription")}
          </div>
        )}
        <div className="space-y-4">
          {variant === "mobile" ? <MobileViewToggle value={mobileView} onChange={setMobileView} /> : null}
          {variant === "mobile" && mobileView === "sheet" ? <MobileSheetView /> : null}
          <div className={cn("space-y-4", variant === "mobile" && mobileView === "sheet" && "hidden")}>
          <PrescriptionTypeSelector kind={editorKind} onChange={handleKindChange} />
          {prescriptionMode === "exam" ? (
            <ExamPrescriptionComposer
              petId={petId}
              clinicId={clinicId}
              appointmentId={appointmentId}
              medicalRecordId={medicalRecordId}
              onCreated={handlePrescriptionCreated}
              onUpdated={handlePrescriptionUpdated}
              validityInDays={examValidityInDays}
              onValidityInDaysChange={setExamValidityInDays}
              showValidityField
              variant={variant}
              initialPrescription={editingPrescription?.type === "exam" ? editingPrescription : null}
            />
          ) : (
            <Form {...form}>
              <form id={formId} onSubmit={handleMedicalPrescriptionFormSubmit} className="space-y-6">
                {prescriptionMode === "manipulated" ? (
                  <div className="space-y-6">
                    {templateSlot}
                    <ManipulatedFormulaEditor
                      form={form}
                      clinicId={clinicId}
                      mobile={variant === "mobile"}
                      expanded={expanded}
                      onOpen={openNewItem}
                      onClose={closeExpanded}
                      onDraftChange={setDraftPayload}
                    />
                    <FormField
                      control={form.control}
                      name="observations"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("examComposer.complementaryGuidance")}</FormLabel>
                          <FormControl>
                            <Textarea rows={3} placeholder={t("examComposer.complementaryGuidancePlaceholder")} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <PrescriptionMetaFields
                      form={form}
                      kind="manipulated"
                      suggestedRegime={null}
                      mobile={variant === "mobile"}
                    />
                    {saveModelButton}
                  </div>
                ) : (
                  <MedicationListEditor
                    form={form}
                    kind={editorKind === "controlled" ? "controlled" : "standard"}
                    clinicId={clinicId}
                    speciesId={speciesId}
                    mobile={variant === "mobile"}
                    items={(watchedItems ?? []) as PrescriptionItemInput[]}
                    expanded={expanded}
                    suggestedRegime={suggestedRegime}
                    templateSlot={templateSlot}
                    footerSlot={saveModelButton}
                    onOpenNew={openNewItem}
                    onOpenEdit={openEditItem}
                    onCloseExpanded={closeExpanded}
                    onCommit={commitItem}
                    onRemove={removeItem}
                    onDuplicate={duplicateItem}
                    onMove={moveItem}
                    onDraftChange={setDraftItem}
                    onOpenCreateMedication={openCreateMedicationFor}
                  />
                )}

                {(() => {
                  const footerButtons = (
                    <>
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => resetComposerForm(editingPrescription)}
                        disabled={createPrescription.isPending || updatePrescription.isPending}
                      >
                        {isEditMode ? t("composer.resetChanges") : t("composer.clear")}
                      </Button>
                      <Button
                        type="submit"
                        form={formId}
                        disabled={createPrescription.isPending || updatePrescription.isPending}
                      >
                        {(createPrescription.isPending || updatePrescription.isPending) && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        {isEditMode ? t("composer.saveChanges") : t("editor.issue")}
                      </Button>
                    </>
                  );

                  if (variant === "mobile" && !footerContainer) return null;

                  return footerContainer ? (
                    createPortal(footerButtons, footerContainer)
                  ) : (
                    <div
                      className={cn(
                        "flex justify-end gap-2",
                        isInsideDialog && "sticky bottom-0 z-10 bg-card py-3",
                      )}
                    >
                      {footerButtons}
                    </div>
                  );
                })()}
              </form>
            </Form>
          )}
          </div>
          {variant === "mobile" && !footerContainer && prescriptionMode !== "exam" && !expanded ? (
            <MobileActionBar
              onViewSheet={() => setMobileView("sheet")}
              formId={formId}
              submitLabel={isEditMode ? t("composer.saveChanges") : t("editor.issue")}
              pending={createPrescription.isPending || updatePrescription.isPending}
              showSheetButton={mobileView === "edit"}
            />
          ) : null}
        </div>
      </div>

      {!isInsideDialog && (
        <div className="space-y-4">
          <div className="rounded-lg border border-border/70 p-4 md:p-6 space-y-4 bg-white">
            <div className="text-lg font-semibold pb-2">{t("composer.issuedPrescriptions")}</div>
            <div className="space-y-4">
              {isLoadingPrescriptions ? (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("composer.loadingPrescriptions")}
                </div>
              ) : existingPrescriptions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("composer.emptyPrescriptions")}
                </p>
              ) : (
                <div className="space-y-3 text-sm overflow-y-auto max-h-[28rem]">
                  {existingPrescriptions.map((prescription) => (
                    <div
                      key={prescription.id}
                      className="rounded-md border border-border/70 p-3 hover:bg-muted/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge
                            variant={
                              prescription.type === "controlled"
                                ? "destructive"
                                : prescription.type === "manipulated"
                                  ? "default"
                                  : "secondary"
                            }
                          >
                            {prescription.type === "controlled"
                              ? t("composer.badges.controlled")
                              : prescription.type === "manipulated"
                                ? t("composer.tabs.manipulated")
                                : prescription.type === "exam"
                                  ? t("composer.tabs.exam")
                                  : t("composer.tabs.standard")}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {prescription.issued_at
                              ? new Date(prescription.issued_at).toLocaleDateString("pt-BR")
                              : t("print.noDate")}
                          </span>
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {prescription.vet?.full_name ?? t("print.veterinarian")}
                        </span>
                      </div>
                      <Separator className="my-2" />
                      <ul className="space-y-1">
                        {(prescription.type === "exam" ? prescription.exam_items : prescription.items)
                          .slice(0, 3)
                          .map((item) => (
                            <li key={item.id} className="text-xs text-muted-foreground">
                              {prescription.type === "exam"
                                ? (item.exam_name || t("composer.tabs.exam"))
                                : (formatMedicationSummary(item) || t("composer.medication"))}
                            </li>
                          ))}
                      </ul>
                      {(prescription.type === "exam"
                        ? prescription.exam_items.length > 3
                        : prescription.items.length > 3) && (
                          <p className="mt-1 text-xs text-muted-foreground">
                            + {(prescription.type === "exam"
                              ? prescription.exam_items.length - 3
                              : prescription.items.length - 3)} {t("composer.items")}
                          </p>
                        )}
                      <div className="mt-3 flex gap-2">
                        {!prescription.signed_at && !(prescription as any).mapa_prescription_identifier && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="px-3"
                            onClick={() => {
                              setEditingPrescriptionId(prescription.id);
                              setShowPrescriptionDialog(false);
                            }}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            setSelectedPrescriptionId(prescription.id);
                            setShowPrescriptionDialog(true);
                          }}
                        >
                          <Eye className="mr-2 h-3 w-3" />
                          {t("medicalRecords:gallery.open")}
                        </Button>
                        {!prescription.signed_at && !prescription.mapa_prescription_identifier && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-destructive/40 px-3 text-destructive hover:bg-destructive/10 hover:text-destructive"
                            onClick={() => {
                              setPrescriptionToDeleteId(prescription.id);
                              setShowDeletePrescriptionDialog(true);
                            }}
                            aria-label={t("composer.deleteIssuedAria", {
                              date: prescription.issued_at
                                ? new Date(prescription.issued_at).toLocaleDateString("pt-BR")
                                : t("print.noDate"),
                            })}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Prescription View Dialog */}
      {selectedPrescription && (() => {
        const signed = Boolean((selectedPrescription as typeof selectedPrescription & { signed_at?: string | null }).signed_at);
        const mapaLocked = Boolean((selectedPrescription as typeof selectedPrescription & { mapa_prescription_identifier?: string | null }).mapa_prescription_identifier);
        return (
          <PrescriptionViewDialog
            key={selectedPrescription.id}
            open={showPrescriptionDialog}
            onOpenChange={(open) => {
              setShowPrescriptionDialog(open);
              if (!open) {
                setSelectedPrescriptionDraft(null);
              }
            }}
            prescription={selectedPrescription}
            onEdit={!signed && !mapaLocked ? () => {
              setShowPrescriptionDialog(false);
              setEditingPrescriptionId(selectedPrescription.id);
            } : undefined}
            onDelete={!mapaLocked ? () => {
              setShowPrescriptionDialog(false);
              setPrescriptionToDeleteId(selectedPrescription.id);
              setShowDeletePrescriptionDialog(true);
            } : undefined}
            onSign={hasCertificate && !signed
              ? () => handleSignPrescription(selectedPrescription)
              : undefined}
            isSigning={signingPrescriptionId === selectedPrescription.id}
          />
        );
      })()}

      <AlertDialog
        open={showDeletePrescriptionDialog}
        onOpenChange={(open) => {
          setShowDeletePrescriptionDialog(open);
          if (!open && !deletePrescription.isPending) {
            setPrescriptionToDeleteId(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("composer.deleteIssuedTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("composer.deleteIssuedDescription", {
                date: prescriptionToDelete?.issued_at
                  ? new Date(prescriptionToDelete.issued_at).toLocaleDateString("pt-BR")
                  : t("print.noDate"),
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletePrescription.isPending}>
              {t("composer.deleteIssuedCancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeletePrescription}
              disabled={deletePrescription.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePrescription.isPending ? t("composer.deleteIssuedLoading") : t("composer.deleteIssuedConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Profile Data Modal */}
      {showProfileDataModal && (
        <ProfileDataModal
          open={showProfileDataModal}
          onOpenChange={(open) => {
            if (!open) {
              // Se o modal está sendo fechado sem completar, limpar dados pendentes
              setPendingPrescriptionData(null);
            }
            setShowProfileDataModal(open);
          }}
          needsCRMV={validation.validate().needsCRMV}
          needsMAPA={validation.validate().needsMAPA}
          needsAddress={validation.validate().needsAddress}
          onComplete={handleProfileDataComplete}
        />
      )}

      {/* SIPEAGRO Guide Dialog */}
      <SIPEAGROGuideDialog
        open={showSIPEAGRODialog}
        onOpenChange={(open) => {
          setShowSIPEAGRODialog(open);
          // Se o modal está sendo fechado, voltar para o tipo padrão
          if (!open) {
            const currentType = form.getValues("type");
            if (currentType === "controlled") {
              form.setValue("type", "standard");
              setPrescriptionMode("standard");
            }
            setPendingPrescriptionData(null);
          }
        }}
        onConfirm={handleSIPEAGROConfirm}
        onCancel={() => {
          const currentType = form.getValues("type");
          if (currentType === "controlled") {
            form.setValue("type", "standard");
            setPrescriptionMode("standard");
          }
          setPendingPrescriptionData(null);
          setShowSIPEAGRODialog(false);
        }}
      />

      <CreateMedicationDialog
        open={showCreateMedicationDialog}
        onOpenChange={setShowCreateMedicationDialog}
        initialName={medicationNameToCreate}
        onMedicationCreated={handleMedicationCreated}
      />
    </div>
  );
};
